// src/components/modals/EditTransportClaimModal.jsx
import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { X, Calendar, DollarSign, User, FileText } from 'lucide-react'
import TransportClaimService from '../../services/transportClaimService'
import EmployeeService from '../../services/employeeService'
import toast from 'react-hot-toast'

const EditTransportClaimModal = ({ isOpen, onClose, onSuccess, claim }) => {
  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState([])
  const [formData, setFormData] = useState({
    date: '',
    employee: '',
    amount: '',
    note: ''
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (isOpen && claim) {
      fetchEmployees()
      populateForm()
    }
  }, [isOpen, claim])

  const fetchEmployees = async () => {
    try {
      const response = await EmployeeService.getAllEmployees()
      if (response.success && response.data) {
        setEmployees(response.data.employees || response.data)
      }
    } catch (error) {
      console.error('Error fetching employees:', error)
      toast.error('Failed to load employees')
    }
  }

  const populateForm = () => {
    if (!claim) return

    // Format date to YYYY-MM-DD for date input
    const dateObj = new Date(claim.date)
    const formattedDate = dateObj.toISOString().split('T')[0]

    setFormData({
      date: formattedDate,
      employee: claim.employee?._id || claim.employee || '',
      amount: claim.amount?.toString() || '',
      note: claim.note || ''
    })
    setErrors({})
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.date) {
      newErrors.date = 'Date is required'
    }

    if (!formData.employee) {
      newErrors.employee = 'Employee is required'
    }

    if (!formData.amount) {
      newErrors.amount = 'Amount is required'
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)

      const claimData = {
        date: formData.date,
        employee: formData.employee,
        amount: parseFloat(formData.amount),
        ...(formData.note && { note: formData.note })
      }

      const response = await TransportClaimService.updateTransportClaim(claim._id, claimData)

      if (response.success) {
        toast.success(response.message || 'Transport claim updated successfully')
        onSuccess()
      }
    } catch (error) {
      console.error('Error updating transport claim:', error)
      toast.error(error.response?.data?.message || 'Failed to update transport claim')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  if (!isOpen || !claim) return null

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
      style={{ zIndex: 999999 }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit Transport Claim</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="inline h-4 w-4 mr-1" />
              Date *
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors.date ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.date && (
              <p className="mt-1 text-sm text-red-600">{errors.date}</p>
            )}
          </div>

          {/* Employee */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="inline h-4 w-4 mr-1" />
              Employee *
            </label>
            <select
              name="employee"
              value={formData.employee}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors.employee ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Employee</option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.fullName} - {emp.contactNumber}
                </option>
              ))}
            </select>
            {errors.employee && (
              <p className="mt-1 text-sm text-red-600">{errors.employee}</p>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="inline h-4 w-4 mr-1" />
              Amount (LKR) *
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="0.00"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors.amount ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.amount && (
              <p className="mt-1 text-sm text-red-600">{errors.amount}</p>
            )}
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Note (Optional)
            </label>
            <textarea
              name="note"
              value={formData.note}
              onChange={handleChange}
              rows={3}
              placeholder="Enter any additional notes..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Claim'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

EditTransportClaimModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  claim: PropTypes.object
}

export default EditTransportClaimModal
